<template>
  <div class="landing-page">
    <nav
      class="navbar navbar-expand-lg navbar-light bg-white sticky-top shadow-sm py-3"
    >
      <div class="container">
        <router-link class="navbar-brand font-weight-bold text-primary" to="/">
          <i class="fas fa-rocket mr-2"></i>TasksApp with AI Integration
        </router-link>
        <button
          class="navbar-toggler"
          type="button"
          data-toggle="collapse"
          data-target="#navbarNav"
        >
          <span class="navbar-toggler-icon"></span>
        </button>
        <div class="collapse navbar-collapse" id="navbarNav">
          <ul class="navbar-nav ml-auto align-items-center">
            <li class="nav-item">
              <a class="nav-link font-weight-bold" href="#groups"
                >Collaboration</a
              >
            </li>
            <li class="nav-item">
              <a class="nav-link font-weight-bold" href="#security">Security</a>
            </li>
            <li class="nav-item">
              <a class="nav-link font-weight-bold" href="#ai-showcase"
                >AI Features</a
              >
            </li>
            <li class="nav-item ml-lg-3">
              <router-link
                class="btn btn-primary px-4 shadow-sm rounded-pill"
                to="/auth"
                >Get Started</router-link
              >
            </li>
          </ul>
        </div>
      </div>
    </nav>

    <header class="hero-section text-center text-white">
      <div class="container">
        <h1 class="display-3 font-weight-bold mb-3">
          Enterprise Task Management <br />
          <span class="text-cyan">Meet Artificial Intelligence</span>
        </h1>
        <p class="lead mb-4 opacity-90 mx-auto" style="max-width: 800px">
          Build, secure, and optimize your team's workflow with a robust
          Full-Stack architecture (Node.js/MySQL) integrated with Groq LPU™ AI
          technology.
        </p>

        <div
          class="demo-wrapper d-flex align-items-center justify-content-center p-3"
        >
          <div
            class="demo-access-box animate__animated animate__pulse animate__infinite"
          >
            <div class="demo-content text-center">
              <span class="demo-badge mb-2 d-inline-block">QUICK TOUR</span>
              <h5 class="text-white mb-3">
                Experience the full power immediately:
              </h5>

              <div class="credentials-container p-3 mb-3">
                <div class="credential-row mb-2">
                  <i class="fas fa-user-shield text-info"></i>
                  <span class="auth-text ml-2">diraribnsalah@gmail.com</span>
                </div>
                <div class="credential-row">
                  <i class="fas fa-lock text-warning"></i>
                  <span class="auth-text ml-2">azerty</span>
                </div>
              </div>

              <div class="secondary-accounts p-2 rounded shadow-sm mb-3">
                <p class="small text-info-emphasis mb-2">
                  Test team accounts (All pass: <b>azerty</b>)
                </p>
                <div class="accounts-grid">
                  <code class="d-block text-warning mb-1"
                    >ibnsalah.dirar@etu.uae.ac.ma</code
                  >
                  <code class="d-block text-warning"
                    >ibensalahdirar@gmail.com</code
                  >
                </div>
              </div>

              <router-link
                to="/auth"
                class="btn btn-info btn-lg btn-block rounded-pill"
              >
                Login Now <i class="fas fa-arrow-right ml-1"></i>
              </router-link>
            </div>
          </div>
        </div>

        <div class="mockup-container shadow-2xl">
          <img
            src="@/assets/screenshots/task1.jpg"
            @error="handleImgError"
            alt="Main Dashboard"
            class="img-fluid rounded-lg border shadow-lg"
          />
        </div>
      </div>
    </header>

    <section id="groups" class="py-100 bg-white">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-md-5 mb-5 mb-md-0">
            <h2 class="display-4 font-weight-bold text-dark mb-4">
              Smart Team Groups
            </h2>
            <p class="lead text-muted mb-4">
              Create isolated workspaces for different departments. Our dynamic
              MySQL schema supports infinite group creation with role-based
              access control (RBAC).
            </p>
            <div class="d-flex mb-3">
              <div class="icon-small mr-3">
                <i class="fas fa-users text-primary"></i>
              </div>
              <div>
                <h5>Multi-Tenant Workspace</h5>
                <p class="small text-muted">
                  Dedicated data isolation for every team group created.
                </p>
              </div>
            </div>
            <div class="d-flex">
              <div class="icon-small mr-3">
                <i class="fas fa-route text-primary"></i>
              </div>
              <div>
                <h5>Dynamic Routing</h5>
                <p class="small text-muted">
                  Vue Router integration for seamless navigation between group
                  contexts.
                </p>
              </div>
            </div>
          </div>
          <div class="col-md-7">
            <div class="image-grid-overlap">
              <img
                v-for="n in 4"
                :key="n"
                :src="getImgUrl(n)"
                class="grid-img shadow-lg border"
                :style="`--order: ${n}`"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="ai-showcase" class="py-5 bg-dark text-white">
      <div class="container py-5">
        <div class="text-center mb-5">
          <h2 class="display-4 font-weight-bold mb-3 text-gradient">
            Screenshots for our web site
          </h2>
          <p class="lead text-cyan opacity-90">
            Explore our intuitive interface through 20+ detailed snapshots,
            showcasing a seamless fusion of modern UI design and powerful
            AI-driven insights.
          </p>
        </div>
        <div class="screenshot-gallery">
          <div v-for="n in 18" :key="n" class="gallery-item shadow-lg">
            <img
              :src="getImgUrl(n)"
              @error="handleImgError"
              :alt="'TaskFlow Feature ' + n"
              class="img-fluid"
            />
            <div class="gallery-overlay">
              <div class="p-3">
                <i class="fas fa-bolt text-warning mb-2 fa-lg"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="security" class="py-5 bg-light border-top">
      <div class="container py-5">
        <div class="text-center mb-5">
          <h2 class="font-weight-bold display-4">Rock-Solid Security Stack</h2>
          <p class="text-muted">
            Built for production with the latest security protocols.
          </p>
        </div>
        <div class="row">
          <div
            class="col-md-4 mb-4"
            v-for="feat in securityFeatures"
            :key="feat.title"
          >
            <div
              class="card h-100 border-0 shadow-sm p-4 text-center hover-up rounded-xl"
            >
              <div :class="`icon-box mb-4 ${feat.color}`">
                <i :class="feat.icon"></i>
              </div>
              <h4 class="font-weight-bold">{{ feat.title }}</h4>
              <p class="text-muted">{{ feat.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="py-5 bg-white text-center">
      <div class="container py-5">
        <h3 class="display-4 font-weight-bold mb-5">Built for Scaling</h3>
        <p class="lead text-muted mb-5">
          Our Relational Database Schema ensures data integrity and high-speed
          queries.
        </p>

        <div class="tech-stack-badges mt-5">
          <span
            v-for="tech in [
              'Node.js',
              'Vue 3',
              'MySQL',
              'Express',
              'JWT',
              'Bcrypt',
              'Axios',
              'Bootstrap',
            ]"
            :key="tech"
            class="badge-custom"
            >{{ tech }}</span
          >
        </div>
      </div>
    </section>

    <footer class="footer-cta py-5 bg-primary text-white text-center">
      <div class="container py-4">
        <h2 class="display-4 font-weight-bold mb-4">
          Ready to automate your team?
        </h2>
        <router-link
          to="/auth"
          class="btn btn-light btn-lg px-5 py-3 font-weight-bold text-primary rounded-pill shadow-lg hover-scale"
        >
          Create Your Account
        </router-link>
        <div class="mt-5 opacity-50">
          <p>
            &copy; 2026 TasksApp with AI Assistant. All rights reserved.<br />Full-Stack
            Engineering Showcase.
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: "LandingPageView",
  data() {
    return {
      securityFeatures: [
        {
          title: "JWT Authentication",
          desc: "Secure Stateless Auth using JSON Web Tokens with local storage management.",
          icon: "fas fa-id-badge",
          color: "text-primary",
        },
        {
          title: "Bcrypt Encryption",
          desc: "Industry-standard password hashing before saving to MySQL database.",
          icon: "fas fa-user-shield",
          color: "text-success",
        },
        {
          title: "Protected Routes",
          desc: "Middleware-based route guards preventing unauthorized navigation.",
          icon: "fas fa-door-closed",
          color: "text-warning",
        },
      ],
    };
  },
  methods: {
    getImgUrl(index) {
      try {
        return require(`@/assets/screenshots/task${index}.jpg`);
      } catch (e) {
        return `https://via.placeholder.com/600x400/2a5298/ffffff?text=TaskFlow+Screen+${index}`;
      }
    },
    handleImgError(e) {
      e.target.src =
        "https://via.placeholder.com/800x600/2a5298/ffffff?text=System+Preview+Available";
    },
  },
};
</script>

<style scoped>
.text-cyan {
  color: #6dd5ed !important;
}
.text-gradient {
  background: linear-gradient(45deg, #6dd5ed, #ffffff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.py-100 {
  padding: 100px 0;
}
.hero-section {
  background: linear-gradient(
    rgba(42, 82, 152, 0.96),
    rgba(102, 126, 234, 0.92)
  );
  padding: 140px 0 120px;
  clip-path: polygon(0 0, 100% 0, 100% 88%, 0% 100%);
}

.demo-access-box {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: 20px;
  display: inline-block;
  margin-bottom: 40px;
  max-width: 500px;
}
.demo-badge {
  background: #6dd5ed;
  color: #1a2a6c;
  font-size: 0.7rem;
  font-weight: 800;
  padding: 2px 12px;
  border-radius: 50px;
  letter-spacing: 1px;
}
.demo-text {
  margin-top: 10px;
  font-weight: 500;
  font-size: 1.1rem;
}
.demo-credentials {
  display: flex;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  padding: 10px 20px;
  align-items: center;
  justify-content: center;
}
.credential-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Courier New", monospace;
  font-size: 0.9rem;
}
.credential-item i {
  font-size: 0.8rem;
  opacity: 0.7;
}
.credential-sep {
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.2);
  margin: 0 15px;
}

.mockup-container {
  transform: translateY(80px);
  transition: 0.6s ease;
  max-width: 900px;
  margin: 0 auto;
}
.screenshot-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 25px;
}
.gallery-item {
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  background: #222;
  transition: 0.4s;
  cursor: pointer;
  height: 200px;
}
.gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: 0.5s;
}
.gallery-item:hover img {
  transform: scale(1.1);
  filter: blur(2px);
}
.gallery-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(42, 82, 152, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: 0.4s;
}
.gallery-item:hover .gallery-overlay {
  opacity: 1;
}

.image-grid-overlap {
  position: relative;
  height: 450px;
}
.grid-img {
  position: absolute;
  width: 75%;
  border-radius: 15px;
  transition: 0.4s;
  border: 4px solid white !important;
}
.grid-img:nth-child(1) {
  top: 0;
  left: 0;
  z-index: 4;
}
.grid-img:nth-child(2) {
  top: 50px;
  left: 50px;
  z-index: 3;
}
.grid-img:nth-child(3) {
  top: 100px;
  left: 100px;
  z-index: 2;
}
.grid-img:nth-child(4) {
  top: 150px;
  left: 150px;
  z-index: 1;
}

.badge-custom {
  background: #f0f4f8;
  color: #2a5298;
  padding: 10px 20px;
  border-radius: 50px;
  font-weight: bold;
  margin: 5px;
  display: inline-block;
  border: 1px solid #d1d9e6;
}
.icon-box {
  width: 80px;
  height: 80px;
  line-height: 80px;
  background: #fff;
  border-radius: 50%;
  margin: 0 auto;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
}
.footer-cta {
  clip-path: polygon(0 10%, 100% 0, 100% 100%, 0% 100%);
  padding-top: 120px;
}
</style>
